import React, { useState } from 'react';
import styled from 'styled-components';

const PoemsContainer = styled.div`
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  margin-bottom: 20px;
  position: relative;
`;

const PageTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 40px;
  font-size: 2.5rem;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 15px;
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'var(--light-text)' : 'var(--primary-color)'};
  border: 2px solid var(--primary-color);
  padding: 8px 20px;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--secondary-color)'};
    border-color: ${props => props.active ? 'var(--primary-color)' : 'var(--secondary-color)'};
    color: var(--light-text);
  }
`;

const PoemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
`;

const PoemCard = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  padding: 25px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const PoemTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  color: var(--primary-color);
  margin-bottom: 15px;
  font-size: 1.4rem;
`;

const PoemContent = styled.p`
  color: var(--dark-text);
  line-height: 1.6;
  margin-bottom: 20px;
  flex-grow: 1;
  white-space: pre-line;
`;

const PoemAuthor = styled.p`
  color: var(--secondary-color);
  font-style: italic;
  text-align: right;
  margin-top: auto;
`;

const OriginalText = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: #666;
  margin-top: 15px;
  padding: 10px;
  border-left: 3px solid var(--accent-color);
  background-color: rgba(0,0,0,0.03);
  white-space: pre-line;
`;

// Données des poèmes
const poemsData = [
  {
    id: 1,
    title: "Mon père, ce héros…",
    content: "Mon père, ce héros au sourire si doux,\nMe disait : \"Mon enfant, sois fort et sois juste.\"\nEt je l'écoutais, car il était mon maître,\nEt je l'aimais, car il était mon père.",
    author: "Jacques Prévert (France)",
    length: "short",
    isOriginal: true
  },
  {
    id: 2,
    title: "Those Winter Sundays",
    content: "Les dimanches aussi, mon père se levait tôt\nEt s'habillait dans le froid bleu-noir,\nPuis avec ses mains gercées, douloureuses\nDu labeur de la semaine, il faisait\nFlamboyer le feu. Personne ne le remerciait.",
    author: "Robert Hayden (États-Unis)",
    original: "Sundays too my father got up early\nand put his clothes on in the blueblack cold,\nthen with cracked hands that ached\nfrom labor in the weekday weather made\nbanked fires blaze. No one ever thanked him.",
    length: "short",
    isOriginal: false
  },
  {
    id: 3,
    title: "A mi padre",
    content: "Cher vieil homme, je pense à toi maintenant\nEn ce soir qui s'en va,\nEt je ne sais si tu es au ciel,\nDans l'oubli ou dans le néant.",
    author: "Jorge Luis Borges (Argentine)",
    original: "Querido viejo, te pienso ahora\nen esta tarde que se va,\ny que no sé si estás en el cielo\no en el olvido o en la nada.",
    length: "short",
    isOriginal: false
  },
  {
    id: 4,
    title: "Mon père",
    content: "Mon père, toi qui marchais dans la nuit sans lune,\nToi qui portais au cœur l'étoile de l'espoir,\nJe te salue, ô père, toi qui m'as donné\nLa force et la fierté d'être un homme.",
    author: "Léopold Sédar Senghor (Sénégal)",
    length: "short",
    isOriginal: true
  },
  {
    id: 5,
    title: "Mein Vater",
    content: "Mon père était un homme silencieux,\nMais ses yeux en disaient long.\nIl m'a montré le monde,\nSans dire un seul mot.",
    author: "Rainer Maria Rilke (Allemagne)",
    original: "Mein Vater war ein stiller Mann,\nDoch seine Augen sprachen Bände.\nEr zeigte mir die Welt,\nOhne ein Wort zu sagen.",
    length: "short",
    isOriginal: false
  },
  {
    id: 6,
    title: "Mio padre",
    content: "Mon père était un homme simple,\nMais dans ses gestes résidait toute la poésie\nDu monde.",
    author: "Giuseppe Ungaretti (Italie)",
    original: "Mio padre era un uomo semplice,\nMa nei suoi gesti c'era tutta la poesia\nDel mondo.",
    length: "short",
    isOriginal: false
  },
  {
    id: 7,
    title: "Мой отец",
    content: "Mon père, tu étais comme un chêne,\nSolide, sage, éternel.\nTon ombre réchauffe encore\nMon cœur aujourd'hui.",
    author: "Boris Pasternak (Russie)",
    original: "Мой отец, ты был как дуб,\nКрепкий, мудрый, вечный.\nТень твоя до сих пор\nГреет мое сердце.",
    length: "short",
    isOriginal: false
  },
  {
    id: 8,
    title: "父",
    content: "Les mains de mon père, rudes mais chaudes,\nOnt soutenu tout mon ciel.",
    author: "Lu Xun (Chine)",
    original: "父亲的手，粗糙却温暖，\n撑起了我整个天空。",
    length: "short",
    isOriginal: false
  },
  {
    id: 9,
    title: "Ode to My Father",
    content: "Mon père, tu fus la mer\nQui m'a appris à nager dans la vie.\nMaintenant que tu es parti,\nJe sens ton courant dans mon sang.",
    author: "Pablo Neruda (Chili)",
    original: "Padre mío, fuiste el mar\nque me enseñó a nadar en la vida.\nAhora que te has ido,\nsiento tu corriente en mi sangre.",
    length: "short",
    isOriginal: false
  },
  {
    id: 10,
    title: "Mon père, ce jardinier",
    content: "Mon père était un jardinier,\nIl plantait la patience dans ma terre\nEt attendait les fleurs dans mes yeux.",
    author: "Mahmoud Darwich (Palestine)",
    original: "أبي كان بستانياً\nيزرع الصبر في تربتي\nوينتظر الأزهار في عينيّ",
    length: "short",
    isOriginal: false
  },
  {
    id: 11,
    title: "Un père est comme un arbre",
    content: "Un père est comme un arbre :\nSes racines nourrissent,\nSes branches protègent,\nEt son ombre dure\nBien après sa mort.",
    author: "Proverbe touareg",
    length: "short",
    isOriginal: true
  },
  {
    id: 12,
    title: "L'héritage",
    content: "Ce n'est pas l'argent ni les biens\nQue tu m'as donnés comme héritage,\nMais la sagesse et le courage\nQui guident chacun de mes chemins.\n\nTes conseils comme une boussole,\nTes valeurs comme une carte au trésor,\nTu m'as offert bien plus que de l'or :\nUne vie riche de ton exemple.",
    author: "Marie Dubois",
    length: "medium",
    isOriginal: true
  },
  {
    id: 13,
    title: "Merci Papa",
    content: "Pour les histoires du soir,\nPour les vélos réparés,\nPour les larmes essuyées,\nPour les rires partagés,\nMerci Papa.",
    author: "Lucas Martin",
    length: "short",
    isOriginal: true
  },
  {
    id: 14,
    title: "Le temps avec toi",
    content: "Le temps passé avec toi\nEst comme un trésor précieux\nQue je garde au fond de moi\nComme un joyau silencieux.\n\nChaque moment, chaque sourire,\nChaque conseil, chaque leçon,\nSont gravés dans mes souvenirs\nComme une éternelle chanson.",
    author: "Sophie Leclerc",
    length: "medium",
    isOriginal: true
  },
  {
    id: 15,
    title: "L'homme que je suis devenu",
    content: "L'homme que je suis devenu\nTe doit tellement, mon père\nTes valeurs, ta droiture, ta force\nOnt façonné mon caractère.\n\nTu m'as appris à me relever\nQuand la vie me mettait à terre\nÀ persévérer, à rêver\nÀ être fier et sincère.\n\nAujourd'hui je marche dans tes pas\nEspérant te rendre honneur\nEt transmettre à mon tour\nCe que tu as mis dans mon cœur.",
    author: "Thomas Moreau",
    length: "medium",
    isOriginal: true
  },
  {
    id: 16,
    title: "Le chêne et le roseau",
    content: "Tu es comme un chêne robuste\nDont les racines profondes\nAncrent notre famille dans ce monde\nOffrant stabilité et confiance.\n\nTu plies parfois comme le roseau\nFace aux tempêtes de la vie\nMais jamais tu ne romps\nMontrant ta force dans la souplesse.\n\nJ'apprends de toi chaque jour\nComment être fort sans être rigide\nComment aimer sans condition\nComment vivre avec sagesse.",
    author: "Claire Fontaine",
    length: "medium",
    isOriginal: true
  },
  {
    id: 17,
    title: "Ode à mon père",
    content: "Dans le grand livre de ma vie\nTu es le chapitre fondateur\nCelui qui donne sens à l'histoire\nCelui qui éclaire les suivants.\n\nTes mains ont bâti notre maison\nTon cœur a construit notre foyer\nTa voix a chanté mes premiers rêves\nTon âme a guidé mes premiers pas.\n\nDans les moments de doute et de peine\nTu étais là, solide comme un roc\nDans les instants de joie et de fête\nTu étais là, heureux et fier.\n\nAujourd'hui je te célèbre, père\nNon pas seulement pour ce jour dédié\nMais pour tous les jours où tu as été\nSimple et grand à la fois, mon héros ordinaire.",
    author: "Philippe Dubois",
    length: "long",
    isOriginal: true
  },
  {
    id: 18,
    title: "Les petits riens",
    content: "Ce sont les petits riens\nQui font de toi un grand père :\nLe café préparé le matin\nLes histoires racontées hier\nLes conseils donnés sans insister\nLes silences qui parlent\nLes regards qui encouragent\nLes mains qui soutiennent.\n\nCe sont les petits riens\nQui construisent l'amour immense\nQue je te porte, père,\nEt que je porterai toujours.",
    author: "Émilie Renard",
    length: "medium",
    isOriginal: true
  },
  {
    id: 19,
    title: "L'empreinte",
    content: "Tu as laissé ton empreinte\nSur le chemin de ma vie\nUne trace indélébile\nQui me guide et m'inspire.\n\nTu as semé des graines de sagesse\nDans le jardin de mon esprit\nElles fleurissent aujourd'hui\nEn pensées et en actes.\n\nTu as forgé mon caractère\nSur l'enclume de ton exemple\nAvec le marteau de ta patience\nEt le feu de ton amour.\n\nJe suis fier de porter ton nom\nDe marcher dans tes pas\nEt d'honorer ton héritage\nChaque jour de ma vie.",
    author: "Nicolas Leroy",
    length: "long",
    isOriginal: true
  }
];

const Poems = () => {
  const [filter, setFilter] = useState('all');
  
  const filteredPoems = filter === 'all' 
    ? poemsData 
    : poemsData.filter(poem => poem.length === filter);
  
  return (
    <PoemsContainer>
      <PageTitle>Des poèmes pour toi, Papa</PageTitle>
      
      <FilterContainer>
        <FilterButton 
          active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          Tous
        </FilterButton>
        <FilterButton 
          active={filter === 'short'} 
          onClick={() => setFilter('short')}
        >
          Courts
        </FilterButton>
        <FilterButton 
          active={filter === 'medium'} 
          onClick={() => setFilter('medium')}
        >
          Moyens
        </FilterButton>
        <FilterButton 
          active={filter === 'long'} 
          onClick={() => setFilter('long')}
        >
          Longs
        </FilterButton>
      </FilterContainer>
      
      <PoemsGrid>
        {filteredPoems.map(poem => (
          <PoemCard key={poem.id}>
            <PoemTitle>{poem.title}</PoemTitle>
            <PoemContent>{poem.content}</PoemContent>
            
            {/* Afficher le texte original pour les poèmes traduits */}
            {!poem.isOriginal && poem.original && (
              <OriginalText>
                <strong>Texte original:</strong><br />
                {poem.original}
              </OriginalText>
            )}
            
            <PoemAuthor>— {poem.author}</PoemAuthor>
          </PoemCard>
        ))}
      </PoemsGrid>
    </PoemsContainer>
  );
};

export default Poems;
